// Markdown Parser for Market Vision Technical Dictionary
// Extracts structured metadata, formulas, ASCII diagrams, and descriptions from .md files

export function parseMarkdownDoc(mdContent, id) {
    if (!mdContent || typeof mdContent !== "string") return null;

    // 1. Extract title (# ...)
    const titleMatch = mdContent.match(/^#{1,3}\s+(.+)$/m);
    const term = titleMatch ? titleMatch[1].trim() : id;

    // 2. Extract Category (**Category:** ...)
    const categoryMatch = mdContent.match(/\*\*Category:\*\*\s*([^\n\r]+)/i);
    const category = categoryMatch ? categoryMatch[1].trim().replace(/\s+$/, "") : (id.includes('plan') || id.includes('management') ? "Playbook" : "Market Context");

    // 3. Extract Rule / Formula (**Rule / Formula:** ...)
    const ruleMatch = mdContent.match(/\*\*(?:Rule\s*\/\s*Formula|Main\s*rule|Order):\*\*\s*(?:`([^`]+)`|([^\n\r]+))/i);
    const ruleOrFormula = ruleMatch ? (ruleMatch[1] || ruleMatch[2] || "").trim() : "";

    // 4. Extract Short Definition (## Short Definition ...)
    const shortDefMatch = mdContent.match(/##\s+Short Definition\s*\n+([\s\S]*?)(?=\n+(?:---+\s*\n+)?###?\s+[A-Za-z0-9]|\n+---+|$)/i);
    let shortDef = shortDefMatch ? shortDefMatch[1].trim() : "";
    if (shortDef.includes("---")) {
        shortDef = shortDef.split(/---+/)[0].trim();
    }
    if (shortDef.includes("###") || shortDef.includes("##")) {
        shortDef = shortDef.split(/###?/)[0].trim();
    }
    if (!shortDef) {
        const orderMatch = mdContent.match(/\*\*Order:\*\*\s*([^\n\r]+)/i);
        if (orderMatch) {
            shortDef = `Order: ${orderMatch[1].trim()}`;
        }
    }

    // 5. Extract Images (![alt](url) or <img src="...">)
    // Only extract carousel illustrations if there is an explicit Illustration section
    // or if the images appear outside the main fullDescription body
    const illustrationSectionMatch = mdContent.match(/##\s+Illustration\s*\n+([\s\S]*?)(?=\n+(?:---+\s*\n+)?##\s+[A-Za-z]|$)/i);
    let images = [];
    if (illustrationSectionMatch) {
        const illMatches = [...illustrationSectionMatch[1].matchAll(/!\[(.*?)\]\((.*?)\)/g)];
        images = illMatches.map(m => ({ caption: m[1].trim(), url: m[2].trim() }));
    } else {
        const allImageMatches = [...mdContent.matchAll(/!\[(.*?)\]\((.*?)\)/g)];
        const hasDetailedDesc = /##\s+(?:Detailed Rules & Mechanics|Rules & Mechanics|Core Principles|Detailed Analysis)/i.test(mdContent);
        if (!hasDetailedDesc) {
            images = allImageMatches.map(m => ({ caption: m[1].trim(), url: m[2].trim() }));
        }
    }
    const htmlImgMatch = mdContent.match(/<img[^>]+src=["']([^"']+)["'][^>]*alt=["']?([^"'>]*)["']?[^>]*>/i);
    const imageUrl = images[0]?.url || (htmlImgMatch ? htmlImgMatch[1].trim() : "");
    const imageCaption = images[0]?.caption || (htmlImgMatch ? htmlImgMatch[2].trim() : "");

    // 5b. Extract Structural Diagram (```text ... ```)
    const diagramMatch = mdContent.match(/##\s+Structural Diagram\s*\n+[\s\S]*?```(?:text)?\s*\n([\s\S]*?)```/i);
    const diagram = diagramMatch ? diagramMatch[1].trimEnd() : "";

    // 5c. Extract raw SVG if present
    const svgMatch = mdContent.match(/```(?:svg|xml)\s*\n([\s\S]*?)```/i) || mdContent.match(/(<svg[\s\S]*?<\/svg>)/i);
    const svg = svgMatch ? svgMatch[1].trim() : "";

    // 6. Extract Detailed Rules & Mechanics or main body content
    let fullDescription = "";
    const explicitDescMatch = mdContent.match(/##\s+(?:Detailed Rules & Mechanics|Rules & Mechanics|Core Principles|Detailed Analysis)\s*\n+([\s\S]*?)(?=\n+(?:---+\s*\n+)?##\s+(?:Structural Diagram|Illustration|Strategic Application)|$)/i);
    if (explicitDescMatch) {
        fullDescription = explicitDescMatch[1].trim();
    } else {
        const bodyMatch = mdContent.match(/(?:##\s+Short Definition\s*\n+[\s\S]*?\n+(?:---+\s*\n+)?)([\s\S]*?)(?=\n+(?:---+\s*\n+)?##\s+(?:Structural Diagram|Illustration|Strategic Application)|$)/i);
        if (bodyMatch) {
            fullDescription = bodyMatch[1].trim();
        } else {
            const afterSepMatch = mdContent.match(/(?:---+\s*\n+)([\s\S]*?)(?=\n+(?:---+\s*\n+)?##\s+(?:Structural Diagram|Illustration|Strategic Application)|$)/i);
            if (afterSepMatch) {
                fullDescription = afterSepMatch[1].trim();
            } else {
                fullDescription = mdContent.trim();
            }
        }
    }

    // 7. Extract Strategic Application (## Strategic Application ...)
    const appMatch = mdContent.match(/##\s+Strategic Application\s*\n+([\s\S]*?)(?=\n+(?:---+\s*\n+)?##\s+[A-Za-z]|$)/i);
    const application = appMatch ? appMatch[1].trim() : "";

    // 8. Auto-generate tags from title and category
    const tags = Array.from(new Set([
        category.toLowerCase().replace(/&/g, "").split(/\s+/),
        term.toLowerCase().replace(/[^a-z0-9\s]/g, "").split(/\s+/)
    ].flat().filter(t => t.length > 2))).slice(0, 5);

    return {
        id,
        term,
        category,
        shortDef,
        ruleOrFormula,
        imageUrl,
        imageCaption,
        images,
        svg,
        diagram,
        fullDescription,
        application,
        tags
    };
}
